import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Calculator } from 'lucide-react';
import { DIRECTIONAL_BASELINE_LABEL } from '@siliconx/shared';
import type { AssetFmvOutlookResponse } from '@siliconx/shared';
import type { ClientFmvSnapshot } from '../../asset-detail/asset-detail-types';
import { FmvOutlookChart, monthLong } from '@siliconx/shared/ui/FmvOutlookChart';
import { FmvBasisTrigger } from '../FmvBasisDrawer';
import { api, throwIfNotOk } from '../../../lib/api';
import { formatDate } from '../../../lib/format';
import { qk } from '../../../lib/queryKeys';
import { Card, CardLabel, TileLabel, MiniTable, downloadCsv, money, collapseSameDayDuplicates, type ApiError } from '../shared';
import { OrgFeature } from '../../../lib/orgFeatures';

/**
 * SIL-565: Value tab (formerly "Market value") — FMV outlook chart, hold-cost
 * stat, and FMV history. Split out of AssetDetailV2.tsx; content unchanged
 * from the prior inline render (SIL-335 origin).
 *
 * Was a single forecast-chart card; the tab is now the home for FMV context
 * that used to be stranded above the tab bar (the always-visible FmvTile
 * keeps the headline figure/delta), or nowhere (a legend, a labeled
 * hold-cost stat). The chart itself restates its own data series in the
 * emerald ramp — DESIGN.md reserves amber and violet as semantic-only in this
 * portal, and violet/amber as a plain categorical palette on this chart was
 * exactly that kind of decorative use. Amber now marks only the Price cliff
 * marker, which is a genuine warning moment.
 */

const MARKET_VALUE_SERIES_COLOR = '#047857'; // emerald-700 — FMV actual/forecast line + cone
const MARKET_VALUE_BOOK_COLOR = '#6ee7b7'; // emerald-300 — book-depreciation reference line
const MARKET_VALUE_CLIFF_COLOR = '#d97706'; // amber-600 — reserved strictly for the cliff marker

/** Colored-swatch legend for the outlook chart's two series + uncertainty
 * band + cliff marker. A customer-portal-local row rather than a change to
 * the shared chart's rendering (SIL-335) — it stays out of the shared
 * component's props contract, so the operations portal (which keeps its own
 * violet brand palette on this chart) is untouched. */
function OutlookLegend({ hasCliff }: { hasCliff: boolean }) {
  const items: { label: string; swatch: React.ReactNode }[] = [
    {
      label: 'FMV actual',
      swatch: <span className="h-0.5 w-3.5 rounded-full" style={{ background: MARKET_VALUE_SERIES_COLOR }} />,
    },
    {
      label: 'FMV forecast',
      swatch: (
        <span
          className="h-0 w-3.5 border-t-2 border-dashed"
          style={{ borderColor: MARKET_VALUE_SERIES_COLOR }}
        />
      ),
    },
    {
      label: 'Uncertainty range',
      swatch: <span className="size-2.5 rounded-sm" style={{ background: MARKET_VALUE_SERIES_COLOR, opacity: 0.25 }} />,
    },
    {
      label: 'Book value',
      swatch: (
        <span
          className="h-0 w-3.5 border-t-2 border-dashed"
          style={{ borderColor: MARKET_VALUE_BOOK_COLOR }}
        />
      ),
    },
  ];
  if (hasCliff) {
    items.push({
      label: 'Price cliff',
      swatch: (
        <span
          className="h-0 w-3.5 border-t-2 border-dashed"
          style={{ borderColor: MARKET_VALUE_CLIFF_COLOR }}
        />
      ),
    });
  }
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-zinc-500">
      {items.map((it) => (
        <span key={it.label} className="flex items-center gap-1.5">
          {it.swatch}
          {it.label}
        </span>
      ))}
    </div>
  );
}

/** Headline "held to <month>" hold-cost stat — was an 11px mono caption
 * buried in the shared card's header; promoted to a labeled stat block so
 * it reads as the tab's single most decision-relevant number (SIL-335). */
/** SIL-1828 (R2): the server-derived outlook flag, rendered as the label only. */
function DirectionalPill() {
  return (
    <span
      data-testid="outlook-directional-label"
      className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700"
    >
      {DIRECTIONAL_BASELINE_LABEL}
    </span>
  );
}

function HoldCostStat({ outlook }: { outlook: AssetFmvOutlookResponse }) {
  const delta = outlook.heldDeltaUsd;
  const lastMonth = outlook.forecast[outlook.forecast.length - 1]?.month;
  if (delta == null || !lastMonth) return null;
  const negative = delta < 0;
  return (
    <Card className="p-5">
      <TileLabel>Hold-cost forecast</TileLabel>
      <div className={`mt-2 font-mono text-2xl font-semibold ${negative ? 'text-amber-700' : 'text-emerald-700'}`}>
        {negative ? '−' : '+'}
        {money(Math.abs(delta))}
      </div>
      <div className="mt-1 text-xs text-zinc-500">
        Projected median FMV change if held to {monthLong(lastMonth)}
      </div>
      {outlook.isDirectionalBaseline && (
        <div className="mt-1.5">
          <DirectionalPill />
        </div>
      )}
    </Card>
  );
}

/** Outlook chart card — customer-local composition (not the shared
 * `FmvOutlookCard`) so the emerald recolor and legend stay portal-local and
 * the shared component's props/rendering contract is untouched for ops. */
function FmvOutlookChartCard({
  history,
  outlook,
}: {
  history: ClientFmvSnapshot[];
  outlook: AssetFmvOutlookResponse;
}) {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between gap-2">
        <CardLabel>FMV outlook if held</CardLabel>
        {outlook.isDirectionalBaseline && <DirectionalPill />}
      </div>
      <div className="mt-3.5">
        <FmvOutlookChart
          history={history.map((h) => ({ pricedAt: h.pricedAt, fmvMedUsd: h.fmvMedUsd }))}
          outlook={outlook}
          seriesColor={MARKET_VALUE_SERIES_COLOR}
          bookColor={MARKET_VALUE_BOOK_COLOR}
          cliffColor={MARKET_VALUE_CLIFF_COLOR}
        />
      </div>
      <OutlookLegend hasCliff={outlook.refreshCliff != null} />
      <div className="mt-2.5 text-xs text-zinc-500">
        Shaded band = uncertainty range · selling ahead of the refresh cliff captures most of
        today&apos;s value.
      </div>
    </Card>
  );
}

/** FMV history table + "show the math" basis trigger — moved into this tab
 * from the always-visible FmvTile's collapsed "View table" toggle, so the
 * evidence behind the figure lives on the tab that's about market value
 * (SIL-335). Reuses `FmvBasisTrigger`, the same basis-drawer affordance
 * SIL-334 wired into FmvTile, rather than building a second one. */
function FmvHistoryCard({ history: rawHistory }: { history: ClientFmvSnapshot[] }) {
  const [showAll, setShowAll] = useState(false);
  const history = collapseSameDayDuplicates(rawHistory);
  const latest = history[0] ?? null;
  const visible = showAll ? history : history.slice(0, 8);
  return (
    <Card>
      <div className="flex items-center justify-between">
        <CardLabel>FMV history</CardLabel>
        <div className="flex items-center gap-3">
          <FmvBasisTrigger snapshot={latest} />
          <button
            type="button"
            onClick={() =>
              downloadCsv(
                'fmv-history.csv',
                ['Date', 'FMV median'],
                history.map((r) => [formatDate(r.pricedAt), r.fmvMedUsd]),
              )
            }
            className="text-xs font-semibold text-zinc-500 hover:text-zinc-700"
          >
            Export CSV
          </button>
        </div>
      </div>
      {history.length === 0 ? (
        <p className="mt-3 text-sm text-zinc-400">No FMV snapshots yet.</p>
      ) : (
        <>
          <MiniTable
            header={['Date', 'FMV median']}
            rows={visible.map((r) => [
              formatDate(r.pricedAt),
              // SIL-1828 (R2): server flag, label only.
              r.isDirectionalBaseline ? `${money(r.fmvMedUsd)} (Directional)` : money(r.fmvMedUsd),
            ])}
            numericCols={[1]}
          />
          {!showAll && history.length > 8 && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="mt-1.5 text-xs text-zinc-500 hover:text-zinc-700"
            >
              Showing 8 of {history.length} · view all
            </button>
          )}
        </>
      )}
    </Card>
  );
}

/** SIL-566 (I2-B4) — cross-pillar deep link: this asset's class, preselected
 * on a brand-new scenario cohort (`?classId=` — see the scenario wizard's
 * `STAGE_SEARCH`). Sits in the Value tab, not Overview, because "model this
 * class in a scenario" is a market-value decision, same reason this tab owns
 * the FMV outlook and hold-cost stat above.
 *
 * SIL-1036: `orgFeature: 'scenarios'` — an individual customer_org has no
 * what-if scenario workflow to deep-link into; the caller wraps this in
 * `<OrgFeature name="scenarios">`. */
function ScenarioDeepLink({ assetClassId }: { assetClassId: string }) {
  return (
    <Link
      to="/financials/scenarios/$scenarioId"
      params={{ scenarioId: 'new' }}
      search={{ stage: 1, tab: 'lifecycle', classId: assetClassId }}
      className="inline-flex items-center gap-1.5 self-start text-xs font-semibold text-brand-700 hover:underline"
    >
      <Calculator className="size-3.5" /> Model this class in a scenario
    </Link>
  );
}

export function ValueTab({
  assetId,
  assetClassId,
  history,
}: {
  assetId: string;
  assetClassId: string;
  history: ClientFmvSnapshot[];
}) {
  const { data } = useQuery<AssetFmvOutlookResponse, ApiError>({
    queryKey: qk.assets.fmvOutlook(assetId),
    queryFn: async () => {
      const res = await api.api.client['fmv-outlook'].assets[':assetId'].$get({
        param: { assetId },
      });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as AssetFmvOutlookResponse;
    },
  });
  const hasOutlook = data && (data.forecast.length > 0 || history.length > 0);
  const hasHoldCost =
    data != null && data.heldDeltaUsd != null && data.forecast.length > 0;
  return (
    <>
      {hasOutlook && data && (
        <div
          className={
            hasHoldCost ? 'grid grid-cols-1 gap-3 lg:grid-cols-[1fr_2fr]' : 'grid grid-cols-1 gap-3'
          }
        >
          {hasHoldCost && <HoldCostStat outlook={data} />}
          <FmvOutlookChartCard history={history} outlook={data} />
        </div>
      )}
      <OrgFeature name="scenarios">
        <div className="flex justify-end">
          <ScenarioDeepLink assetClassId={assetClassId} />
        </div>
      </OrgFeature>
      <FmvHistoryCard history={history} />
    </>
  );
}
